/* eslint-disable no-debugger */
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';
import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';
import useSelectOrgUnitData from '@usas/ui-web/state/selectOrgUnit/UseSelectOrgUnitData';

import useTouchpointTypeData from '../../../../state/touchpointType/UseTouchpointTypeData';
import useTouchpointSubjectData from '../../../../state/touchpointSubject/UseTouchpointSubjectData';
import useLscTouchpointData from '../../../../state/lscTouchpoints/UseLscTouchpointsData';
import useClubStaffData from "../../../../state/clubStaff/UseClubStaffData";
import useLscBoardMemberData from '../../../../state/lscBoardMembers/UseLscBoardMemberData';

import { navLinks } from '../../UseMemberAdministration';

import validate from './EditLSCTouchpointValidation';

const useEditLSCTouchpoint = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { touchpointTypeState } = useTouchpointTypeData();
  const { touchpointSubjectState } = useTouchpointSubjectData();
  const { selectOrgUnitState } = useSelectOrgUnitData();

  const { lscTouchpointState, putLscTouchpoint } = useLscTouchpointData();
  const { getClubStaffMembers } = useClubStaffData();
  const { getLscBoardMembers } = useLscBoardMemberData();
  const [lscStaffOptionsState, setLscStaffOptionsState] = useState({ items: [], areItemsLoaded: false });
  const [lscBoardMemberOptionsState, setLscBoardMemberOptionsState] = useState({ items: [], areItemsLoaded: false });

  const { formState, errorState, isSubmitting, updateFormState, onFormValueChanged, onValueTextPairChanged, handleSubmit, setErrors, setFormData, setIsDirty
  } = useForm(getInitialFormState, onSubmitFormCallback, validate);

  function onSubmitFormCallback() {
    const touchpointObj = {
      touchpointTypeId: formState.touchpointTypeId,
      touchpointSubjectId: formState.touchpointSubjectId,
      touchpointNote: formState.touchpointNote,
      touchpointDate: formState.touchpointDate,
      followUpDate: formState.followUpDate === Constants.BLANK_DATE_STRING ? null : formState.followUpDate,
      staffMemberId: formState.memberId || '',
      hasCustomerSatisfactionSurvey: formState.hasCustomerSatisfactionSurvey || false
    };

    const putLscTouchpointPromise = putLscTouchpoint(formState.lscTouchpointId, touchpointObj);
    if (putLscTouchpointPromise !== null) {
      putLscTouchpointPromise.then((newState) => {
        if (newState !== null) {
          //nothing to do here
          navigate(navLinks.LSC_TOUCHPOINTS);
        }
      }).catch((e) => {
        //TODO context error
      });
    }
  }

  const onSaveClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    handleSubmit();
  };

  const onCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(navLinks.LSC_TOUCHPOINTS);
  };

  useEffect(() => {
    if (
      location.state?.touchpointTypeId &&
      touchpointTypeState.isLoaded &&
      touchpointSubjectState.isLoaded &&
      lscBoardMemberOptionsState.areItemsLoaded === true
    ) {
      setFormData({
        ...formState,
        lscTouchpointId: location.state?.lscTouchpointId,
        touchpointTypeId: location.state?.touchpointTypeId,
        touchpointSubjectId: location.state?.touchpointSubjectId,
        touchpointNote: location.state?.touchpointNote,
        touchpointDate: formatDate(location.state?.touchpointDate) || Constants.BLANK_DATE_STRING,
        followUpDate: formatDate(location.state?.followUpDate) || Constants.BLANK_DATE_STRING,
        memberId: location.state?.staffMemberId || '',
        hasCustomerSatisfactionSurvey: location.state?.hasCustomerSatisfactionSurvey || false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchpointTypeState, touchpointSubjectState, location, lscBoardMemberOptionsState]);

  useEffect(() => {
    if (selectOrgUnitState.orgUnitId) {
      const getClubStaffMembersPromise = getClubStaffMembers(selectOrgUnitState.orgUnitId, 2, false);

      if (getClubStaffMembersPromise !== null) {
        getClubStaffMembersPromise.then((newStaffState) => {
          if (newStaffState !== null) {
            const getLscBoardMembersPromise = getLscBoardMembers(selectOrgUnitState.orgUnitId);

            if (getLscBoardMembersPromise !== null) {
              getLscBoardMembersPromise.then((newBoardMemberState) => {
                if (newBoardMemberState !== null) {
                  let combinedItems = [];
                  let staffItems = [];
                  let boardItems = [];

                  newBoardMemberState.arrayData.forEach(boardMember => {  
                    let cntCurrentRoles = 0;
                    boardMember.orgRoles.forEach(role => {
                      if (new Date(role.expirationDate) >= new Date()) {
                        cntCurrentRoles++;
                      } 
                    });
                    if (cntCurrentRoles > 0) {
                      boardItems.push(boardMember);
                    }
                  }); 
                  
                  newStaffState.arrayData.forEach(staff => {  
                    let cntCurrentRoles = 0;
                    staff.personOrgRole.forEach(role => {
                      if (new Date(role.expirationDate) >= new Date()) {
                        cntCurrentRoles++;
                      }
                    });
                    if (cntCurrentRoles > 0) {
                      staff.lastName = staff.lastName + ' (Staff Only)';
                      staffItems.push(staff);
                    }
                  });

                  combinedItems = boardItems.concat(staffItems);

                  //sort by last name then first name
                  combinedItems.sort((a, b) => {
                    if (a.lastName < b.lastName) return -1;
                    if (a.lastName > b.lastName) return 1;
                    return 0;
                  });

                  const items = combinedItems.map(x => { return { id: x.memberId, name: `${x.firstName} ${x.lastName}` } });

                  //remove duplicates
                  const uniqueItems = Array.from(new Set(items.map(item => item.id)))
                    .map(id => {
                      return items.find(item => item.id === id);
                    });

                  uniqueItems.unshift({ id: '', name: '--' });

                  setLscBoardMemberOptionsState({
                    ...lscBoardMemberOptionsState,
                    items: uniqueItems,
                    areItemsLoaded: true
                  });
                }
              }).catch((e) => {
                //TODO context error
              });
            }
          }
        }).catch((e) => {
          //TODO context error
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectOrgUnitState]);

  function getInitialFormState() {
    return {
      lscTouchpointId: Constants.DEFAULT_ID,
      touchpointTypeId: Constants.DEFAULT_ID,
      touchpointTypeName: '',
      touchpointSubjectId: Constants.DEFAULT_ID,
      touchpointSubjectName: '',
      touchpointNote: '',
      touchpointDate: '',
      followUpDate: '',
      memberId: '',
      memberName: '',
      hasCustomerSatisfactionSurvey: false
    };
  }
  return {
    formState,
    errorState,
    touchpointTypeState,
    touchpointSubjectState,
    lscTouchpointState,
    lscStaffOptionsState,
    lscBoardMemberOptionsState,
    location,
    onFormValueChanged,
    onValueTextPairChanged,
    handleSubmit,
    onSaveClicked,
    onCancelClicked,
    isLoading: touchpointTypeState.isLoading || touchpointSubjectState.isLoading,
  };
}

export default useEditLSCTouchpoint;